import { useTranslations } from "next-intl";
import SectionHeader from "./SectionHeader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components-shared/ui/dialog";
import React, { useState } from "react";
import { cn } from "@/lib/utils";
import CircularProgress from "@/components-shared/CircularProgress";
import { CircleAlert } from "lucide-react";
import _360deg from "@/public/images/360deg.svg";

const VrTour = ({ view360url }: { view360url?: string | null }) => {
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = React.useState(false);

  return (
    <Dialog {...{ open, onOpenChange: setOpen }}>
      <DialogTrigger asChild>
        <button
          className={cn(
            "flex justify-between items-center rounded-2xl p-4 bg-white shadow-lg shadow-gray-300",
            { "[visibility:hidden]": !view360url }
          )}
        >
          <div>
            <SectionHeader>{t("vr.title")}</SectionHeader>
            <p className="text-gray-light text-sm">{t("vr.description")}</p>
          </div>
          <svg
            width="200"
            height="100"
            viewBox="0 0 38 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-[150px] md:w-[200px]"
          >
            <path
              d="M23.8339 24.6196C23.2913 24.6196 22.8205 24.2163 22.7501 23.664C22.6741 23.0647 23.0979 22.5172 23.6969 22.441C27.153 22.0006 30.248 21.0641 32.4116 19.8034C34.3903 18.6507 35.4797 17.2955 35.4797 15.9876C35.4797 14.546 34.2049 13.3665 33.135 12.6305C32.6375 12.2881 32.5115 11.6075 32.8539 11.1095C33.1963 10.6119 33.8773 10.4861 34.3749 10.8284C36.5288 12.3101 37.6672 14.094 37.6672 15.9879C37.6672 18.1371 36.2308 20.1102 33.513 21.6936C31.0666 23.1188 27.768 24.1277 23.9734 24.611C23.9265 24.6167 23.8798 24.6196 23.8339 24.6196ZM18.734 23.0527L15.8174 20.1361C15.3901 19.7088 14.6977 19.7088 14.2704 20.1361C13.8434 20.5631 13.8434 21.2558 14.2704 21.6827L15.1226 22.535C11.8551 22.1974 8.88658 21.4366 6.58803 20.3312C4.00373 19.0885 2.52148 17.5051 2.52148 15.9876C2.52148 14.7004 3.58278 13.3626 5.5096 12.2204C6.02949 11.9125 6.20085 11.2414 5.89299 10.7219C5.58485 10.2021 4.91373 10.0306 4.39427 10.3385C1.03836 12.3277 0.333984 14.5085 0.333984 15.9876C0.333984 18.4143 2.21844 20.6571 5.64012 22.3029C8.29581 23.5796 11.7312 24.435 15.4739 24.766L14.2704 25.9694C13.8434 26.3964 13.8434 27.0891 14.2704 27.5163C14.372 27.618 14.4926 27.6986 14.6254 27.7535C14.7581 27.8084 14.9004 27.8366 15.0441 27.8364C15.3237 27.8364 15.6037 27.7297 15.8174 27.5163L18.734 24.5997C19.161 24.1724 19.161 23.4797 18.734 23.0527Z"
              fill="#00AE41"
            />
            <path
              d="M11.79 13.7982V13.5349C11.79 12.6067 11.2218 12.4267 10.4598 12.4267C9.98879 12.4267 9.83639 12.011 9.83639 11.5956C9.83639 11.1797 9.98879 10.7641 10.4598 10.7641C10.9863 10.7641 11.5405 10.6949 11.5405 9.57264C11.5405 8.7691 11.0833 8.57514 10.5151 8.57514C9.83639 8.57514 9.49004 8.74154 9.49004 9.28185C9.49004 9.75267 9.28208 10.0714 8.47861 10.0714C7.48111 10.0714 7.35664 9.8635 7.35664 9.19843C7.35664 8.11803 8.13226 6.71868 10.5151 6.71868C12.2748 6.71868 13.6047 7.35583 13.6047 9.22629C13.6047 10.2375 13.2307 11.1797 12.538 11.4984C13.3554 11.8032 13.9511 12.4127 13.9511 13.5349V13.7982C13.9511 16.0702 12.3856 16.9293 10.446 16.9293C8.06306 16.9293 7.14868 15.4746 7.14868 14.3108C7.14868 13.6874 7.41191 13.521 8.17382 13.521C9.06048 13.521 9.28215 13.715 9.28215 14.2416C9.28215 14.8928 9.89188 15.0452 10.5152 15.0452C11.4573 15.0452 11.79 14.6988 11.79 13.7982ZM22.3606 13.5349V13.6597C22.3606 16.0426 20.8781 16.9293 18.9664 16.9293C17.0546 16.9293 15.558 16.0426 15.558 13.6597V9.98827C15.558 7.60535 17.0958 6.71868 19.0911 6.71868C21.4324 6.71868 22.3607 8.1733 22.3607 9.32319C22.3607 9.98819 22.0419 10.1959 21.3492 10.1959C20.7536 10.1959 20.227 10.0435 20.227 9.40632C20.227 8.87993 19.673 8.60278 19.0218 8.60278C18.2045 8.60278 17.7193 9.03233 17.7193 9.98819V11.2349C18.1628 10.7502 18.7863 10.6253 19.4514 10.6253C21.0305 10.6253 22.3606 11.318 22.3606 13.5349ZM17.7193 13.8121C17.7193 14.768 18.1905 15.1836 18.9664 15.1836C19.7423 15.1836 20.1994 14.768 20.1994 13.8121V13.6874C20.1994 12.6759 19.7422 12.288 18.9524 12.288C18.2044 12.288 17.7193 12.6483 17.7193 13.5626V13.8121ZM24.0369 13.6597V9.98827C24.0369 7.60535 25.5192 6.71868 27.4312 6.71868C29.3431 6.71868 30.8392 7.60535 30.8392 9.98827V13.6597C30.8392 16.0426 29.343 16.9293 27.4312 16.9293C25.5192 16.9293 24.0369 16.0426 24.0369 13.6597ZM28.6779 9.98827C28.6779 9.03233 28.2071 8.60285 27.4312 8.60285C26.6553 8.60285 26.1982 9.03233 26.1982 9.98827V13.6597C26.1982 14.6156 26.6553 15.0451 27.4312 15.0451C28.207 15.0451 28.6779 14.6156 28.6779 13.6597V9.98827ZM33.4648 6.70898C31.6552 6.70898 30.1836 5.23694 30.1836 3.42773C30.1836 1.61853 31.6552 0.146484 33.4648 0.146484C35.274 0.146484 36.7461 1.61853 36.7461 3.42773C36.7461 5.23694 35.274 6.70898 33.4648 6.70898ZM33.4648 2.33398C32.8615 2.33398 32.3711 2.82471 32.3711 3.42773C32.3711 4.03097 32.8615 4.52148 33.4648 4.52148C34.0678 4.52148 34.5586 4.03097 34.5586 3.42773C34.5586 2.82471 34.0678 2.33398 33.4648 2.33398Z"
              fill="#00AE41"
            />
          </svg>
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl p-2 pt-4">
        <DialogHeader>
          <DialogTitle
            className={cn("text-xl font-semibold leading-none tracking-tight")}
          >
            {t("unitDetails.view360")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex justify-center h-[550px] items-center size-full overflow-hidden rounded-xl">
          {/* Show loader if loading */}
          {loading && !err && <CircularProgress size="lg" />}

          {/* Show warning icon if there's an error */}
          {err && <CircleAlert className="text-gray-500 !size-32" />}

          {view360url && (
            <iframe
              className={cn("h-full w-full", {
                hidden: loading || err,
              })}
              src={view360url}
              allowFullScreen
              allow="xr-spatial-tracking"
              onLoad={() => setLoading(false)} // Update loading state on success
              onError={() => {
                setErr(true); // Update error state
                setLoading(false); // Stop the loader
              }}
            ></iframe>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
export default VrTour;
